import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AtSign, KeyRound } from "lucide-react";
import { toast } from "sonner";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1649731000184-7ced04998f44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxib3V0aXF1ZSUyMGhvdGVsJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzgxMDY0MzA5fDA&ixlib=rb-4.1.0&q=85";

export default function AuthPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const [lEmail, setLEmail] = useState("");
  const [lPwd, setLPwd] = useState("");
  const [twofa, setTwofa] = useState(false);
  const [code, setCode] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(lEmail, lPwd, code);
    setBusy(false);
    if (res.twofa) {
      setTwofa(true);
      setCode("");
      toast.info("Enter the 6-digit code from your authenticator app");
      return;
    }
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      navigate(res.user.role === "tenant" ? "/tenant" : "/landlord");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] bg-ivory text-ink" data-testid="auth-page">
      {/* Left — Ivory editorial panel */}
      <div className="relative flex flex-col px-8 sm:px-16 lg:px-20 py-12">
        {/* Top bar — logo + wordmark + tiny gold rule */}
        <div className="flex items-center gap-3">
          <img src="/logo.jpg" alt="RojasProperty" className="h-11 w-11 rounded-full object-cover ring-1 ring-bone" />
          <div>
            <div className="font-display text-xl tracking-tight leading-none">RojasProperty</div>
            <div className="text-[10px] tracking-[0.3em] text-gold mt-1.5 uppercase font-semibold">Est. — Private Estates</div>
          </div>
          <div className="hidden md:block flex-1 ml-6 border-t border-gold/40" />
        </div>

        {/* Headline */}
        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
          <div className="mb-10">
            <div className="micro-label text-gold mb-5">A Calm, Curated Workspace</div>
            <h1 className="font-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
              Manage every property with <em className="text-gold not-italic font-normal">quiet</em> precision.
            </h1>
            <p className="mt-6 text-slate-600 text-sm leading-relaxed max-w-sm">
              One workspace for rent, maintenance, incidents, insurance &amp; paperwork — composed for landlords who&apos;d rather not chase spreadsheets.
            </p>
          </div>

          {/* Sign-in form */}
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label className="micro-label text-ink">Email</Label>
              <div className="relative">
                <AtSign className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
                <Input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={lEmail}
                  onChange={(e) => setLEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-9 h-12 rounded-none border-bone bg-white focus-visible:ring-1 focus-visible:ring-gold focus-visible:border-gold"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="micro-label text-ink">Password</Label>
              <div className="relative">
                <KeyRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
                <Input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={lPwd}
                  onChange={(e) => setLPwd(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 h-12 rounded-none border-bone bg-white focus-visible:ring-1 focus-visible:ring-gold focus-visible:border-gold"
                />
              </div>
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full h-12 rounded-none bg-ink text-white hover:bg-gold hover:text-ink transition-colors duration-300 tracking-[0.25em] uppercase text-xs font-semibold"
            >
              {busy ? "Signing in…" : (twofa ? "Verify code" : "Sign In")}
            </Button>
          </form>
          {twofa && (
            <div className="mt-3">
              <Label className="micro-label text-ink">2FA Code (6 digits)</Label>
              <Input
                data-testid="login-totp-input"
                autoFocus
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123 456"
                className="h-12 rounded-none border-gold/60 bg-white focus-visible:ring-1 focus-visible:ring-gold mt-1.5 text-center font-mono-num text-lg tracking-[0.4em]"
              />
              <p className="text-[10px] text-slate-500 mt-1.5 text-center">Use your authenticator app, then click Verify code above.</p>
            </div>
          )}

          {/* Notice */}
          <div className="mt-10 pt-6 border-t border-gold/40">
            <p className="text-xs text-slate-500 leading-relaxed text-center" data-testid="auth-invite-only-notice">
              <span className="micro-label text-ink block mb-1.5">By Invitation Only</span>
              Accounts are created by the administrator.<br />
              Need access? Please contact your landlord.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 flex items-end justify-between text-[10px] tracking-[0.2em] text-slate-400 uppercase">
          <span>© RojasProperty</span>
          <span className="text-gold">Vol. I · 2026</span>
        </div>
      </div>

      {/* Right — Full-bleed editorial photography */}
      <div className="relative hidden lg:block overflow-hidden bg-ink">
        <img
          src={HERO_IMAGE}
          alt="Boutique interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/60" />

        {/* Editorial caption overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <div className="micro-label text-gold mb-3">Feature · Estate №01</div>
          <h2 className="font-display text-3xl leading-tight tracking-tight max-w-sm">
            &ldquo;A house is made of walls and beams. A home, of light and care.&rdquo;
          </h2>
          <div className="mt-4 flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase opacity-80">
            <span className="h-px w-8 bg-gold" />
            <span>The Rojas Editorial</span>
          </div>
        </div>
      </div>
    </div>
  );
}
